import type { ApiEndpoints, ProjectDocumentHealSummary } from '@avisoma/shared';

type ProjectRecoverResponse = ApiEndpoints<string>['/api/projects/:id/recover']['POST']['response'];
type ProjectRecoverDocumentResult = ProjectRecoverResponse['documentResults'][number];
// `documentResults` (DR-2461) and `documentLevel` (DR-2601) are both newer than the
// oldest backend this UI may talk to, so both stay optional here for forward/backward
// compatibility — an older backend omits them and the toast still renders.
// Same reason one level down: `classificationRedriven` (DR-2839) is newer than the heal
// summary itself, so a backend that predates it sends the other three lists only.
type CompatibleProjectDocumentHealSummary = Omit<ProjectDocumentHealSummary, 'classificationRedriven'> & {
  classificationRedriven?: string[];
};
type CompatibleProjectRecoverResponse = Omit<ProjectRecoverResponse, 'documentResults' | 'documentLevel'> & {
  documentResults?: ProjectRecoverDocumentResult[];
  documentLevel?: CompatibleProjectDocumentHealSummary;
};

export type RecoverProjectToast = {
  type: 'success' | 'info' | 'error';
  message: string;
};

type ToastSeverity = 'success' | 'info' | 'error';

/** Clauses plus the severity floor the page-level or heal part contributes. */
type ToastParts = { clauses: string[]; severity: ToastSeverity };

const SEVERITY_RANK: Record<ToastSeverity, number> = { success: 0, info: 1, error: 2 };

const maxSeverity = (a: ToastSeverity, b: ToastSeverity): ToastSeverity =>
  SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;

const pluralise = (count: number, noun: string) => `${noun}${count === 1 ? '' : 's'}`;

const aggregateParts = ({
  documentsRecovered,
  documentsFailed,
  pagesRecovered,
}: CompatibleProjectRecoverResponse): ToastParts => ({
  clauses: [
    documentsRecovered > 0
      ? `Recovery requested for ${documentsRecovered} ${pluralise(documentsRecovered, 'document')} covering ${pagesRecovered} ${pluralise(pagesRecovered, 'page')}`
      : null,
    documentsFailed > 0
      ? `Failed to request recovery for ${documentsFailed} ${pluralise(documentsFailed, 'document')}`
      : null,
  ].filter((clause): clause is string => clause !== null),
  severity: documentsFailed > 0 ? 'error' : documentsRecovered > 0 ? 'info' : 'success',
});

const detailedParts = (documentResults: ProjectRecoverDocumentResult[]): ToastParts => {
  const fastCompleted = documentResults.filter((result) => result.outcome === 'fast-completed');
  const asyncStarted = documentResults.filter((result) => result.outcome === 'async-started');
  const alreadyRunning = documentResults.filter((result) => result.outcome === 'already-running');
  const scopeConflict = documentResults.filter((result) => result.outcome === 'scope-conflict');
  const failed = documentResults.filter((result) => result.outcome === 'failed');
  const pagesRecovered = documentResults.reduce(
    (total, result) => (result.outcome === 'fast-completed' ? total + result.pages : total),
    0,
  );

  const clauses = [
    fastCompleted.length > 0
      ? `Recovered ${pagesRecovered} ${pluralise(pagesRecovered, 'page')} across ${fastCompleted.length} ${pluralise(fastCompleted.length, 'document')}`
      : null,
    asyncStarted.length > 0
      ? `Recovery started for ${asyncStarted.length} ${pluralise(asyncStarted.length, 'document')}`
      : null,
    alreadyRunning.length > 0
      ? `Recovery already in progress for ${alreadyRunning.length} ${pluralise(alreadyRunning.length, 'document')}`
      : null,
    scopeConflict.length > 0
      ? `Recovery already in progress elsewhere for ${scopeConflict.length} ${pluralise(scopeConflict.length, 'document')}`
      : null,
    failed.length > 0
      ? `Failed to start recovery for ${failed.length} ${pluralise(failed.length, 'document')}`
      : null,
  ].filter((clause): clause is string => clause !== null);

  const severity: ToastSeverity =
    failed.length > 0
      ? 'error'
      : asyncStarted.length > 0 || alreadyRunning.length > 0 || scopeConflict.length > 0
        ? 'info'
        : 'success';

  return { clauses, severity };
};

/** DR-2601: the document-level heal pass appended after the page-sweep clauses. */
const healParts = (documentLevel: CompatibleProjectDocumentHealSummary | undefined): ToastParts => {
  if (!documentLevel) {
    return { clauses: [], severity: 'success' };
  }
  // DR-2839: the classification re-drive counts as a re-drive like the other three
  // branches — an older backend omits the field, so read it defensively.
  const redriven =
    documentLevel.reconstructionRedriven.length +
    (documentLevel.classificationRedriven?.length ?? 0) +
    documentLevel.entityExtractionRedriven.length +
    documentLevel.rollupReconciled.length;
  const failed = documentLevel.failed.length;

  const clauses = [
    redriven > 0 ? `${redriven} stuck ${pluralise(redriven, 'document')} re-driven` : null,
    failed > 0 ? `${failed} ${pluralise(failed, 'document')} failed to heal` : null,
  ].filter((clause): clause is string => clause !== null);

  return { clauses, severity: failed > 0 ? 'error' : redriven > 0 ? 'info' : 'success' };
};

export const formatRecoverProjectToast = (recoveryResponse: CompatibleProjectRecoverResponse): RecoverProjectToast => {
  const { documentResults } = recoveryResponse;

  const pageSweep = documentResults === undefined ? aggregateParts(recoveryResponse) : detailedParts(documentResults);
  const heal = healParts(recoveryResponse.documentLevel);

  const clauses = [...pageSweep.clauses, ...heal.clauses];
  if (clauses.length === 0) {
    return { type: 'success', message: 'Nothing to recover' };
  }

  return { type: maxSeverity(pageSweep.severity, heal.severity), message: `${clauses.join('. ')}.` };
};
